/**
 * InvoiceComparisonPanel - Inline panel for comparing invoices
 *
 * Displays invoice comparison data in tabs:
 * - Table: Always visible, formatted table of invoice amounts
 * - Chart: Conditionally shown when chartVisible prop is true
 */

import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '@erp/shared-utils';
import type {
  InvoiceData,
  ComparisonData,
  ChartData,
} from '@/components/accounts-payable/InvoiceComparisonDialog';

interface InvoiceComparisonPanelProps {
  comparisonData: ComparisonData | null;
  chart: ChartData | null;
  chartVisible?: boolean;
  defaultTab?: 'table' | 'chart';
}

// Custom tooltip formatter for currency values
const currencyFormatter = (value: number) => formatCurrency(value);

export function InvoiceComparisonPanel({
  comparisonData,
  chart,
  chartVisible = false,
  defaultTab = 'table',
}: InvoiceComparisonPanelProps) {
  if (!comparisonData || !chart) return null;

  const renderBarChart = () => {
    // Detect data keys (excluding 'name')
    const dataKeys = Object.keys(chart.data[0] || {}).filter(
      (key) => key !== 'name',
    );

    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chart.data}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
            label={{
              value: 'Invoice Date',
              position: 'insideBottomRight',
              offset: -10,
            }}
          />
          <YAxis tickFormatter={currencyFormatter} tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(value) => [
              typeof value === 'number' ? formatCurrency(value) : String(value),
              '',
            ]}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
            isAnimationActive={true}
            animationDuration={500}
          />
          <Legend />
          {dataKeys.map((key, index) => (
            <Bar
              key={key}
              dataKey={key}
              name={formatKeyName(key)}
              fill={
                index === 0 ? chart.color : chart.secondaryColor || chart.color
              }
              radius={[4, 4, 0, 0]}
              isAnimationActive={true}
              animationDuration={500}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderLineChart = () => {
    // Detect data keys (excluding 'name')
    const dataKeys = Object.keys(chart.data[0] || {}).filter(
      (key) => key !== 'name',
    );

    return (
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chart.data}
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12 }}
            label={{
              value: 'Invoice Date',
              position: 'insideBottomRight',
              offset: -10,
            }}
          />
          <YAxis tickFormatter={currencyFormatter} tick={{ fontSize: 11 }} />
          <Tooltip
            formatter={(value) => [
              typeof value === 'number' ? formatCurrency(value) : 'N/A',
              '',
            ]}
            contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
            isAnimationActive={true}
            animationDuration={500}
          />
          <Legend />
          {dataKeys.map((key, index) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              name={formatKeyName(key)}
              stroke={
                index === 0 ? chart.color : chart.secondaryColor || chart.color
              }
              strokeWidth={2}
              dot={{ r: 4 }}
              connectNulls={false}
              isAnimationActive={true}
              animationDuration={500}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderChart = () => {
    switch (chart.chartType) {
      case 'bar':
        return renderBarChart();
      case 'line':
        return renderLineChart();
      default:
        return <div>Unsupported chart type</div>;
    }
  };

  // Helper to format camelCase keys to readable labels
  const formatKeyName = (key: string): string => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase());
  };

  const renderTableTab = () => {
    const allInvoices = [
      { ...comparisonData.currentInvoice, isCurrent: true },
      ...comparisonData.previousInvoices.map((inv) => ({
        ...inv,
        isCurrent: false,
      })),
    ].sort(
      (a, b) =>
        new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime(),
    );

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left px-4 py-2 font-semibold">Invoice</th>
              <th className="text-left px-4 py-2 font-semibold">Date</th>
              <th className="text-right px-4 py-2 font-semibold">Subtotal</th>
              <th className="text-right px-4 py-2 font-semibold">Tax</th>
              <th className="text-right px-4 py-2 font-semibold">Total</th>
              <th className="text-right px-4 py-2 font-semibold">Delta</th>
            </tr>
          </thead>
          <tbody>
            {allInvoices.map((invoice) => {
              const isCurrent = invoice.isCurrent;
              const delta = isCurrent
                ? null
                : comparisonData.currentInvoice.totalAmount -
                  invoice.totalAmount;
              const deltaPercent =
                delta !== null && invoice.totalAmount > 0
                  ? ((delta / invoice.totalAmount) * 100).toFixed(1)
                  : 0;
              const deltaColor =
                delta !== null && delta > 0 ? 'text-red-600' : 'text-green-600';

              return (
                <tr
                  key={invoice.id}
                  className={`border-b border-gray-100 ${isCurrent ? 'bg-blue-50' : ''}`}
                >
                  <td className={`px-4 py-2 ${isCurrent ? 'font-medium' : ''}`}>
                    {invoice.invoiceNumber}
                  </td>
                  <td className="px-4 py-2">{invoice.invoiceDate}</td>
                  <td className="text-right px-4 py-2">
                    {formatCurrency(invoice.subtotal)}
                  </td>
                  <td className="text-right px-4 py-2">
                    {formatCurrency(invoice.taxAmount)}
                  </td>
                  <td
                    className={`text-right px-4 py-2 ${isCurrent ? 'font-medium' : ''}`}
                  >
                    {formatCurrency(invoice.totalAmount)}
                  </td>
                  <td
                    className={`text-right px-4 py-2 ${isCurrent ? 'text-gray-500' : deltaColor}`}
                  >
                    {isCurrent
                      ? '—'
                      : `${formatCurrency(delta!)} (${deltaPercent}%)`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold">
          Invoice Comparison - {comparisonData.supplierName}
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          Comparing {comparisonData.actualN} previous invoice
          {comparisonData.actualN !== 1 ? 's' : ''} in {comparisonData.currency}
          {comparisonData.actualN < comparisonData.requestedN && (
            <span className="block text-xs text-amber-600 mt-1">
              Only {comparisonData.actualN} previous invoice
              {comparisonData.actualN !== 1 ? 's' : ''} available (requested{' '}
              {comparisonData.requestedN})
            </span>
          )}
        </p>
      </div>

      <Tabs key={defaultTab} defaultValue={defaultTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="table">Table</TabsTrigger>
          {chartVisible && <TabsTrigger value="chart">Chart</TabsTrigger>}
        </TabsList>

        <TabsContent value="table" className="py-4">
          {renderTableTab()}
        </TabsContent>

        {chartVisible && (
          <TabsContent value="chart" className="py-4">
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-medium">{chart.title}</h3>
              <p className="text-xs text-gray-600">{chart.description}</p>
              <div className="mt-4">{renderChart()}</div>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export default InvoiceComparisonPanel;
